import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import Button from 'components/Button'
import ConfirmModal from 'components/ConfirmModal'

import ConnectForm from './ConnectForm'

const STALE_DAYS = 30

const buildConnectedLabel = metadata => {
  if (!metadata) return 'Connected'
  const { accountName, accountEmail } = metadata
  if (accountName && accountEmail) {
    return `Connected as ${accountName} (${accountEmail})`
  }
  if (accountName) return `Connected as ${accountName}`
  if (accountEmail) return `Connected as ${accountEmail}`
  return 'Connected'
}

const isStale = lastValidatedAt => {
  if (!lastValidatedAt) return false
  const last = new Date(lastValidatedAt).getTime()
  if (!last) return false
  const days = (Date.now() - last) / (1000 * 60 * 60 * 24)
  return days >= STALE_DAYS
}

const IntegrationCard = ({
  provider,
  integration,
  onConnect,
  onDisconnect,
  onStartOAuth,
  isConnecting,
  isDisconnecting,
  connectError,
  onResetConnectError
}) => {
  const isConnected = Boolean(integration)
  const isOAuth = provider.authMethod === 'oauth2'
  const [showForm, setShowForm] = useState(false)
  const [oauthStarting, setOauthStarting] = useState(false)
  const [oauthError, setOauthError] = useState(null)
  const [
    showDisconnectConfirm,
    setShowDisconnectConfirm
  ] = useState(false)

  const handleConnect = async credentials => {
    await onConnect({ provider: provider.key, credentials })
    setShowForm(false)
  }

  const handleStartOAuth = async () => {
    if (!onStartOAuth) return
    setOauthError(null)
    setOauthStarting(true)
    try {
      await onStartOAuth(provider.key)
      // Browser navigates away on success; the loading state persists
      // until the redirect happens.
    } catch (err) {
      setOauthError(err)
      setOauthStarting(false)
    }
  }

  const handleDisconnect = () => {
    onDisconnect(provider.key)
  }

  const openForm = () => {
    if (onResetConnectError) onResetConnectError()
    setShowForm(true)
  }
  const closeForm = () => {
    if (onResetConnectError) onResetConnectError()
    setShowForm(false)
  }

  const lastValidatedLabel =
    isConnected && integration.lastValidatedAt
      ? `Last validated ${moment(
          integration.lastValidatedAt
        ).fromNow()}`
      : null

  const stale =
    isConnected && isStale(integration.lastValidatedAt)

  return (
    <div className="p-20 mb-20 border rounded">
      <div className="d-flex justify-content-between align-items-start mb-10">
        <div>
          <p
            className="font-weight-bold mb-0"
            style={{ fontSize: 18 }}
          >
            {provider.name}
          </p>
          {isConnected ? (
            <>
              <p className="text-gray-dark mb-0">
                {buildConnectedLabel(integration.metadata)}
              </p>
              {lastValidatedLabel && (
                <p
                  className="text-gray-dark mb-0"
                  style={{ fontSize: 12 }}
                >
                  {lastValidatedLabel}
                  {stale &&
                    ' · please reconnect to keep things current'}
                </p>
              )}
            </>
          ) : (
            <p className="text-gray-dark mb-0">Not connected</p>
          )}
        </div>
        <div>
          {isConnected ? (
            <Button
              variant="link"
              smallPadding
              onClick={() => setShowDisconnectConfirm(true)}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          ) : isOAuth ? (
            <Button
              variant="secondary"
              smallPadding
              onClick={handleStartOAuth}
              disabled={oauthStarting || !onStartOAuth}
            >
              {oauthStarting
                ? 'Redirecting…'
                : `Connect ${provider.name}`}
            </Button>
          ) : (
            !showForm && (
              <Button
                variant="secondary"
                smallPadding
                onClick={openForm}
              >
                Connect
              </Button>
            )
          )}
        </div>
      </div>

      {!isConnected && !isOAuth && showForm && (
        <div className="mt-15">
          <ConnectForm
            provider={provider}
            onSubmit={handleConnect}
            isSubmitting={isConnecting}
            error={connectError}
          />
          <Button
            variant="link"
            smallPadding
            onClick={closeForm}
            className="mt-10"
          >
            Cancel
          </Button>
        </div>
      )}

      {!isConnected && isOAuth && oauthError && (
        <p className="text-danger mt-15 mb-0">
          {oauthError.message ||
            `Could not start ${provider.name} connection. Please try again.`}
        </p>
      )}

      <ConfirmModal
        show={showDisconnectConfirm}
        toggleModal={() =>
          setShowDisconnectConfirm(prev => !prev)
        }
        title={`Disconnect ${provider.name}?`}
        description={`Your ${provider.name} credentials will be removed. Any existing leadership profiles will remain, but you'll need to reconnect to generate new ones.`}
        confirmText="Disconnect"
        onConfirm={handleDisconnect}
      />
    </div>
  )
}

IntegrationCard.propTypes = {
  provider: PropTypes.object.isRequired,
  integration: PropTypes.object,
  onConnect: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
  onStartOAuth: PropTypes.func,
  isConnecting: PropTypes.bool,
  isDisconnecting: PropTypes.bool,
  connectError: PropTypes.object,
  onResetConnectError: PropTypes.func
}

export default IntegrationCard
